import React, { useState, useEffect } from "react";
import AppLayout from "../../components/layout/AppLayout";
import { salesService } from "../../services/salesService";
import { NavLink } from "react-router-dom";

const SalesHistory = () => {
  // --- MEMORI (STATE): Menyimpan data riwayat dari database ---
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(""); // Memori untuk teks kotak pencarian

  // Jalankan pengambilan data saat halaman pertama kali dibuka
  useEffect(() => {
    fetchSales();
  }, []);

  /**
   * --- LOGIKA: Mengambil Riwayat Penjualan ---
   * Meminta daftar transaksi yang sudah pernah dilakukan ke database.
   */
  const fetchSales = async () => {
    try {
      setLoading(true);
      const data = await salesService.getSalesHistory();
      setSales(data || []);
    } catch (error) {
      console.error("Error fetching sales history:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * --- LOGIKA: Penyaringan (Filtering) ---
   * Mencocokkan data penjualan dengan teks yang diketik di kotak pencarian
   * (berdasarkan Nomor Transaksi atau Nama Pelanggan).
   */
  const filteredSales = sales.filter(
    (s) =>
      s.transaction_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.customer_name &&
        s.customer_name.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
    }).format(val);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AppLayout pageTitle="Riwayat Penjualan">
      <div className="p-4 md:p-8 space-y-6 md:space-y-8">
        {/* AREA HEADER & PENCARIAN */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="font-headline text-2xl md:text-3xl font-extrabold tracking-tight text-on-surface">
              Riwayat Penjualan
            </h2>
            <p className="text-on-surface-variant text-sm mt-1">
              Lihat riwayat penjualan
            </p>
          </div>

          <div className="relative w-full md:w-80">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-lg">
              search
            </span>
            <input
              className="w-full pl-10 pr-4 py-2.5 bg-surface-container-low border border-outline-variant/30 rounded-xl text-sm focus:ring-2 focus:ring-primary-fixed-dim transition-all"
              placeholder="Cari ID atau Pelanggan..."
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* TABEL DATA RIWAYAT */}
        <div className="bg-surface-container-lowest rounded-3xl shadow-sm border border-outline-variant/20 overflow-hidden">
          {/* Tampilan Desktop (Tabel) */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead>
                <tr className="bg-surface-container-low/50">
                  <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter">
                    ID Transaksi
                  </th>
                  <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter">
                    Tanggal & Waktu
                  </th>
                  <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter">
                    Nama Pelanggan
                  </th>
                  <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter">
                    Penjual
                  </th>
                  <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter text-right">
                    Total Harga
                  </th>
                  <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter text-center">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-8 py-20 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-sm text-on-surface-variant font-medium">
                          Memuat Riwayat Penjualan...
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : filteredSales.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-8 py-20 text-center">
                      <div className="flex flex-col items-center gap-4 opacity-50">
                        <span className="material-symbols-outlined text-6xl">
                          history
                        </span>
                        <p className="text-sm text-on-surface-variant font-medium">
                          Belum ada riwayat penjualan.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredSales.map((sale) => (
                    <tr
                      key={sale.id}
                      className="hover:bg-surface-container-low/30 transition-colors group"
                    >
                      <td className="px-8 py-6">
                        {/* Tautan untuk melihat detail Nota */}
                        <NavLink
                          to={`/sales-receipt?id=${sale.id}`}
                          className="text-sm font-bold text-primary hover:text-primary-container hover:underline decoration-2 underline-offset-4 transition-colors font-mono cursor-pointer"
                        >
                          #{sale.transaction_id}
                        </NavLink>
                      </td>
                      <td className="px-8 py-6 text-sm text-on-surface-variant font-medium">
                        {formatDate(sale.created_at)}
                      </td>
                      <td className="px-8 py-6 text-sm font-bold text-on-surface">
                        {sale.customer_name}
                      </td>
                      <td className="px-8 py-6 text-sm text-on-surface-variant">
                        {sale.profiles?.full_name || "Tim Penjualan"}
                      </td>
                      <td className="px-8 py-6 text-sm font-extrabold text-on-surface text-right">
                        {formatCurrency(sale.total)}
                      </td>
                      <td className="px-8 py-6 text-center whitespace-nowrap">
                        <span
                          className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${
                            sale.status === "Completed"
                              ? "bg-green-100 text-green-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {sale.status === "Completed" ? "Selesai" : sale.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Tampilan Mobile (Kartu) */}
          <div className="grid grid-cols-1 gap-4 p-4 md:hidden">
            {loading ? (
              <div className="text-center py-10 text-sm text-on-surface-variant flex flex-col items-center gap-4">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                <p>Memuat Riwayat Penjualan...</p>
              </div>
            ) : filteredSales.length === 0 ? (
              <div className="text-center py-10 text-sm text-on-surface-variant flex flex-col items-center gap-4 opacity-50">
                <span className="material-symbols-outlined text-6xl">history</span>
                <p>Belum ada riwayat penjualan.</p>
              </div>
            ) : (
              filteredSales.map((sale) => (
                <div key={sale.id} className="bg-surface-container-low/30 border border-outline-variant/20 rounded-2xl p-4 flex flex-col gap-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <NavLink to={`/sales-receipt?id=${sale.id}`} className="text-sm font-bold text-primary hover:underline transition-colors font-mono">#{sale.transaction_id}</NavLink>
                      <p className="text-sm font-bold text-on-surface mt-1">{sale.customer_name}</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${sale.status === "Completed" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                      {sale.status === "Completed" ? "Selesai" : sale.status}
                    </span>
                  </div>
                  <div className="flex flex-col text-[10px] text-on-surface-variant mb-2 font-medium">
                    <span>{formatDate(sale.created_at)}</span>
                    <span>Kasir: {sale.profiles?.full_name || "Tim Penjualan"}</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-outline-variant/10">
                    <span className="text-[10px] font-bold text-outline uppercase tracking-widest">Total Harga</span>
                    <span className="text-sm font-extrabold text-on-surface">{formatCurrency(sale.total)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default SalesHistory;
